/**
 * Shared formatting helpers for MarketsPage and OptionsCalculatorPage.
 * Small and dependency-free — kept separate from optionsMath.ts because that
 * module is pure math with no formatting concerns (keeps it trivially
 * testable in isolation).
 */

export function formatCurrency(value: number, fractionDigits = 2): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

/** Signed currency, e.g. "+$1.23" / "-$1.23" — for change columns. */
export function formatSignedCurrency(value: number, fractionDigits = 2): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${formatCurrency(value, fractionDigits)}`
}

/** Signed percentage, e.g. "+1.23%" / "-1.23%". */
export function formatSignedPercent(value: number, fractionDigits = 2): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(fractionDigits)}%`
}

/** Large numbers compacted for market cap / volume columns, e.g. "$2.41T". */
export function formatCompactCurrency(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1e12) return `$${(value / 1e12).toFixed(2)}T`
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `$${(value / 1e3).toFixed(1)}K`
  return formatCurrency(value)
}

/** Compact volume/share counts, e.g. "12.4M". */
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`
  return value.toLocaleString('en-US')
}

/** CSS class for coloring a change/percentage value green (up) or red (down). */
export function changeClass(value: number): string {
  if (value > 0) return 'gd-change gd-change--up'
  if (value < 0) return 'gd-change gd-change--down'
  return 'gd-change'
}

export function formatDateShort(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return isoString
  }
}

export function formatTimestamp(isoString: string): string {
  try {
    return new Date(isoString).toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return isoString
  }
}
